// Máscaras de documento brasileiro (CPF, CEP, RG).
//
// Contrato de armazenamento: guardamos SEMPRE só os dígitos, como STRING.
// A máscara existe só na tela. Dois motivos:
//   1. `cep` estava definido como field_type 'number' e o `Number()` do input
//      comia o zero à esquerda — 01310-100 virava 1310100, sem volta.
//   2. A base já tem 1.694 CPFs em dígitos puros contra 26 pontuados. Gravar
//      com máscara criaria um terceiro formato em vez de convergir.
//
// RG é caso à parte: não existe formato nacional. Varia por estado, aceita
// letras e o dígito X. Por isso não tem máscara nem validação — só aparo.

export type DocumentFieldType = 'cpf' | 'cep' | 'rg';

/**
 * Limite de DÍGITOS por tipo. Precisa ser aplicado no valor gravado, não só na
 * exibição: `formatCpf` corta a máscara em 11, mas sem cortar aqui um paste de
 * 15 dígitos mostraria 11 e salvaria 15.
 * RG não tem tamanho nacional — o 20 é só um teto de sanidade.
 */
export const DOCUMENT_MAX_DIGITS: Record<DocumentFieldType, number> = { cpf: 11, cep: 8, rg: 20 };

/** Tamanho máximo do texto COM máscara, para o atributo maxLength do input. */
export const DOCUMENT_MAX_LENGTH: Record<DocumentFieldType, number> = {
  cpf: 14, // 000.000.000-00
  cep: 9,  // 00000-000
  rg: 20,
};

/** Remove tudo que não é dígito. */
export function cleanDigits(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '');
}

/**
 * Apara pontas, colapsa espaços e mata tabulação.
 * A tabulação aparece de verdade: valores colados de planilha chegaram ao banco
 * como "\t69088-165 ".
 */
export function trimLoose(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

// ── CPF ──────────────────────────────────────────────────────────────────────

/** Máscara progressiva: aceita valor parcial enquanto a pessoa digita. */
export function formatCpf(value: unknown): string {
  const d = cleanDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Valida os dois dígitos verificadores.
 * Serve para AVISAR, nunca para bloquear: a base tem 8 CPFs inválidos em
 * registros antigos, e travar o salvamento impediria editar qualquer outro
 * campo desses negócios.
 */
export function validateCpf(value: unknown): boolean {
  const d = cleanDigits(value);
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false; // 000.000.000-00 e afins

  for (const len of [9, 10] as const) {
    let soma = 0;
    for (let i = 0; i < len; i++) soma += Number(d[i]) * (len + 1 - i);
    const dv = ((soma * 10) % 11) % 10;
    if (dv !== Number(d[len])) return false;
  }
  return true;
}

// ── CEP ──────────────────────────────────────────────────────────────────────

export function formatCep(value: unknown): string {
  const d = cleanDigits(value).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Só confere o tamanho — não existe dígito verificador em CEP.
 * NÃO completamos com zero à esquerda: um CEP com 7 dígitos pode ser um zero
 * perdido ou um número digitado errado, e não há como distinguir.
 */
export function validateCep(value: unknown): boolean {
  return cleanDigits(value).length === 8;
}

// ── RG ───────────────────────────────────────────────────────────────────────

/** Sem máscara de propósito: RG não tem formato nacional. */
export function cleanRg(value: unknown): string {
  return trimLoose(value);
}

// ── Helpers usados pelo renderizador ─────────────────────────────────────────

/** O que aparece na tela. */
export function formatDocument(type: DocumentFieldType, value: unknown): string {
  if (type === 'cpf') return formatCpf(value);
  if (type === 'cep') return formatCep(value);
  return cleanRg(value);
}

/** O que é gravado no banco. Sempre string, sempre dentro do limite. */
export function cleanDocument(type: DocumentFieldType, value: unknown): string {
  if (type === 'cpf' || type === 'cep') {
    return cleanDigits(value).slice(0, DOCUMENT_MAX_DIGITS[type]);
  }
  return cleanRg(value).slice(0, DOCUMENT_MAX_DIGITS.rg);
}

/**
 * Aviso para exibir sob o campo, ou null quando está tudo bem.
 * Campo vazio não gera aviso — quem cobra preenchimento é o gate de etapa.
 */
export function documentWarning(type: DocumentFieldType, value: unknown): string | null {
  const d = cleanDocument(type, value);
  if (!d) return null;

  if (type === 'cpf') {
    if (cleanDigits(d).length < 11) return 'CPF incompleto';
    return validateCpf(d) ? null : 'CPF inválido — confira os números';
  }
  if (type === 'cep') {
    return validateCep(d) ? null : 'CEP deve ter 8 dígitos';
  }
  return null;
}

/**
 * Erro que IMPEDE a gravação, ou null quando pode salvar.
 *
 * Só o CPF bloqueia, porque só ele tem verificação matemática: os dois últimos
 * dígitos são calculados a partir dos nove primeiros, então "inválido" aqui
 * significa impossível, não "não encontrado".
 *
 * CEP e RG apenas avisam (ver `documentWarning`): CEP não tem dígito
 * verificador — só dá para conferir o tamanho, e um CEP de 7 dígitos pode ser um
 * zero perdido legítimo. RG não tem formato nacional.
 *
 * Vazio nunca bloqueia: apagar é ação legítima, e quem exige preenchimento é o
 * gate de qualidade da etapa.
 */
export function documentBlockingError(type: DocumentFieldType, value: unknown): string | null {
  if (type !== 'cpf') return null;

  const d = cleanDocument('cpf', value);
  if (!d) return null;
  if (d.length < 11) return 'CPF incompleto — informe os 11 dígitos';
  return validateCpf(d) ? null : 'CPF inválido — o dígito verificador não confere';
}

export const DOCUMENT_FIELD_TYPES: DocumentFieldType[] = ['cpf', 'cep', 'rg'];

export function isDocumentFieldType(type: string): type is DocumentFieldType {
  return (DOCUMENT_FIELD_TYPES as string[]).includes(type);
}
